/**
 * The Studio drawer's tool panels: Extend, Remix, Replace, Split Stems, Cover Art
 * and the Download Center.
 *
 * Verbatim from the monolith's `activeDrawer === "…"` branches — same copy, same
 * placeholders, same textarea row counts, same button labels.
 *
 * SIMULATED, except Split Stems. Extend, Remix and Replace still queue a credit
 * confirmation and a 1300ms timer that appends a version record — nothing processes
 * audio and no cover image is produced. Split Stems is real: it calls Replicate's
 * Demucs model to separate the song's current audio into vocals, bass, drums,
 * guitar, piano and other (see server/src/services/stemSplitService.ts). The four optional prompts are
 * collected here and flow through studioStore.buildAction() to the API layer so
 * Phase 5 can forward them.
 *
 * The Cover Art panel is built around the artwork box (CoverPreview), which IS the
 * artwork area: it shows the cover, takes a click or a drop to upload one, carries
 * Remove, and carries the Apply for anything staged. It used to be a read-only preview
 * with a separate dashed upload panel underneath and an "or" divider between them —
 * two artwork surfaces in one panel, one that showed artwork and one that accepted it.
 * The form beside it now holds only what a *generation* needs. What a generation
 * produces is still the seeded gradient stand-in rather than an image, and the panel's
 * notice says so; an upload is a real image and the notice is careful not to claim
 * otherwise.
 */
import { useEffect, useRef, useState } from 'react';
import {
  Clock,
  Download,
  FileMusic,
  Image as ImageIcon,
  Layers,
  Loader2,
  Music,
  Pause,
  Play,
  Scissors,
  Wand2,
} from 'lucide-react';
import { Btn, Chip, PrototypeNotice } from '../ui';
import CoverPreview from './CoverPreview';
import Disclosure from './Disclosure';
import DrawerHeader from './DrawerHeader';
import ToolButton from './ToolButton';
import { hasCoverArt } from '../../lib/artwork';
import { COVER_STYLES, REMIX_STYLES } from '../../data/music';
import { STUDIO_COVER_MAX_IMAGE_LABEL } from '../../lib/coverImagePolicy';
import { formatTime, parseLength } from '../../lib/formatting';
import { firstImageFrom } from '../../lib/imageUpload';
import { formatTimecode } from '../../lib/trim';
import { c } from '../../styles/theme';
import type { Song, StudioActionKind } from '../../types/domain';
import type { PendingCover, StudioFormField } from '../../stores/studioStore';

/**
 * Shared panel padding, identical in every tool panel.
 *
 * A class rather than the inline `padding: '18px 20px 32px'` this was, because the
 * 20px sides are too much of a 380px screen and an inline style cannot be narrowed
 * by a media query. See .lyx-tool-panel.
 */
const PANEL_CLASS = 'lyx-tool-panel';

/**
 * Shared field styling, on all four prompt textareas and both Replace inputs.
 *
 * The colours stay inline — they come from the token object, and a field's surface is
 * not something a breakpoint changes. What moved into .lyx-tool-field is the font
 * size, because on a touch device it has to grow to 16px and an inline `fontSize: 13`
 * cannot be overridden by a media query. See the note on that rule.
 */
const FIELD_CLASS = 'lyx-tool-field';

const TEXTAREA: React.CSSProperties = {
  width: '100%',
  background: c.surface2,
  border: `1px solid ${c.border}`,
  borderRadius: 8,
  resize: 'none',
  padding: 10,
  color: c.text1,
  marginBottom: 16,
};

const LABEL: React.CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 8px' };
const DESC: React.CSSProperties = { fontSize: 12, color: c.text2, margin: '0 0 14px' };
const REMIX_LYRICS_NOTICE =
  "Optional lyrics to override the ones extracted from the source song. If not provided, the singer will sing the original song's lyrics. Use \\n for line breaks and structure tags like [verse], [chorus], [bridge] to mark sections. Up to 3000 characters.";

/** Values and setters for the tool forms, from studioStore. */
export interface StudioFormValues {
  extendFromText: string | null;
  extendSecondsText: string;
  extendTemperatureText: string;
  extendVarianceText: string;
  extendPrompt: string;
  remixStyle: string | null;
  remixPrompt: string;
  remixLyricsOverride: string;
  replaceFrom: string;
  replaceTo: string;
  replaceDuration: string;
  replaceSteps: string;
  replacePrompt: string;
  stemsShifts: number;
  coverStyle: string | null;
  coverPrompt: string;
}

export interface StudioToolPanelsProps {
  song: Song;
  form: StudioFormValues;
  setField: (field: StudioFormField, value: string | number | null) => void;
  processing: boolean;
  processingActionKind?: StudioActionKind | null;
  onBack: () => void;
  onClose: () => void;
}

export function ExtendPanel({
  song,
  form,
  setField,
  processing,
  processingActionKind,
  onBack,
  onClose,
  onGenerate,
}: StudioToolPanelsProps & { onGenerate: () => void }) {
  const runtimeDefault = formatTimecode(parseLength(song.length));
  const input: React.CSSProperties = {
    width: '100%',
    background: c.surface2,
    border: `1px solid ${c.border}`,
    borderRadius: 8,
    padding: '10px 12px',
    color: c.text1,
  };

  useEffect(() => {
    if (form.extendFromText == null) setField('extendFromText', runtimeDefault);
  }, [form.extendFromText, runtimeDefault, setField]);

  useEffect(() => {
    if (!form.extendSecondsText.trim()) setField('extendSecondsText', '15');
  }, [form.extendSecondsText, setField]);

  useEffect(() => {
    if (!form.extendTemperatureText.trim()) setField('extendTemperatureText', '1');
  }, [form.extendTemperatureText, setField]);

  useEffect(() => {
    if (!form.extendVarianceText.trim()) setField('extendVarianceText', '3');
  }, [form.extendVarianceText, setField]);

  return (
    <div className={PANEL_CLASS}>
      <DrawerHeader title="Extend Song" onBack={onBack} onClose={onClose} />
      {/*
        THE PROVIDER CLAIM IS GONE, and it was the only one in the app. This line read
        "Uses the Flow Music API" — inherited verbatim from the prototype, and untrue: no
        request is made to Flow Music or to any other provider, and nothing in src/ has
        ever contained a provider URL or credential. It is the one sentence in the Studio
        that named an integration rather than describing a feature, so it is the one that
        could not be defended. See the notice below for what actually happens, and
        docs/api-contract.md's provider boundary for where such a call would belong (behind
        the backend, never in this component).
      */}
      <p style={DESC}>Continue &quot;{song.title}&quot; beyond its original ending.</p>
      <PrototypeNotice style={{ marginBottom: 14, whiteSpace: 'pre-line' }}>
        {"Temperature: Controls the 'conservativeness' of the sampling process. Higher temperature means more diversity.\nVariance: Increases the influence of inputs on the output. Higher values produce lower-varience outputs that adhere more closely to inputs."}
      </PrototypeNotice>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
          <label htmlFor="studio-extend-seconds" style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}>
            Extend by (seconds)
          </label>
          <input
            id="studio-extend-seconds"
            inputMode="numeric"
            value={form.extendSecondsText}
            onChange={(e) => setField('extendSecondsText', e.target.value)}
            className={FIELD_CLASS}
            style={input}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
          <label htmlFor="studio-extend-temperature" style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}>
            Temperature
          </label>
          <input
            id="studio-extend-temperature"
            inputMode="decimal"
            value={form.extendTemperatureText}
            onChange={(e) => setField('extendTemperatureText', e.target.value)}
            className={FIELD_CLASS}
            style={input}
          />
        </div>
        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
          <label htmlFor="studio-extend-variance" style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}>
            Variance
          </label>
          <input
            id="studio-extend-variance"
            inputMode="decimal"
            value={form.extendVarianceText}
            onChange={(e) => setField('extendVarianceText', e.target.value)}
            className={FIELD_CLASS}
            style={input}
          />
        </div>
      </div>
      <label htmlFor="studio-extend-prompt" style={{ ...LABEL, display: 'block' }}>
        Optional instruction
      </label>
      <textarea
        id="studio-extend-prompt"
        rows={3}
        value={form.extendPrompt}
        onChange={(e) => setField('extendPrompt', e.target.value)}
        className={FIELD_CLASS}
        style={TEXTAREA}
      />
      <Btn full onClick={onGenerate} disabled={processing}>
        <Clock size={13} />
        {processing && processingActionKind === 'extend' ? 'Extending song…' : 'Generate'}
      </Btn>
    </div>
  );
}

export function RemixPanel({
  song,
  form,
  setField,
  processing,
  processingActionKind,
  onBack,
  onClose,
  onGenerate,
}: StudioToolPanelsProps & { onGenerate: () => void }) {
  return (
    <div className={PANEL_CLASS}>
      <DrawerHeader title="Remix Song" onBack={onBack} onClose={onClose} />
      <p style={DESC}>
        Generate another version of &quot;{song.title}&quot; with a different feel.
      </p>
      <p style={LABEL}>Style</p>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
        {REMIX_STYLES.map((style) => (
          <Chip
            key={style}
            label={style}
            active={form.remixStyle === style}
            onClick={() => setField('remixStyle', form.remixStyle === style ? null : style)}
          />
        ))}
      </div>
      <div style={{ marginBottom: 14 }}>
        <Disclosure title="Lyrics override" defaultOpen={Boolean(form.remixLyricsOverride)}>
          <label htmlFor="studio-remix-lyrics-override" style={{ ...LABEL, display: 'block', marginTop: 4 }}>
            Optional lyrics override
          </label>
          <textarea
            id="studio-remix-lyrics-override"
            rows={6}
            maxLength={3000}
            value={form.remixLyricsOverride}
            onChange={(e) => setField('remixLyricsOverride', e.target.value)}
            className={FIELD_CLASS}
            style={{ ...TEXTAREA, marginBottom: 10 }}
          />
          <PrototypeNotice>{REMIX_LYRICS_NOTICE}</PrototypeNotice>
        </Disclosure>
      </div>
      <label htmlFor="studio-remix-prompt" style={{ ...LABEL, display: 'block' }}>
        Optional instruction
      </label>
      <textarea
        id="studio-remix-prompt"
        rows={3}
        value={form.remixPrompt}
        onChange={(e) => setField('remixPrompt', e.target.value)}
        className={FIELD_CLASS}
        style={TEXTAREA}
      />
      <Btn full onClick={onGenerate} disabled={processing}>
        <Wand2 size={13} />
        {processing && processingActionKind === 'remix' ? 'Remixing song…' : 'Generate'}
      </Btn>
    </div>
  );
}

export function ReplacePanel({
  song,
  form,
  setField,
  processing,
  processingActionKind,
  onBack,
  onClose,
  onGenerate,
}: StudioToolPanelsProps & { onGenerate: () => void }) {
  const totalDurationSec = Math.max(1, Math.round(parseLength(song.length)));
  const replaceRuntimeSupported = totalDurationSec >= 6 && totalDurationSec <= 190;
  const replaceRuntimeNotice = `Replace is available only for tracks between 6 and 190 seconds long. This song is ${formatTime(totalDurationSec)} long.`;
  const defaultFrom = formatTime(Math.max(0, Math.min(totalDurationSec, Math.round(totalDurationSec / 2))));
  const defaultTo = formatTime(totalDurationSec);
  const defaultDuration = String(totalDurationSec);
  const input: React.CSSProperties = {
    width: '100%',
    background: c.surface2,
    border: `1px solid ${c.border}`,
    borderRadius: 8,
    padding: '8px 10px',
    color: c.text1,
  };

  useEffect(() => {
    if (!form.replaceFrom.trim()) setField('replaceFrom', defaultFrom);
  }, [defaultFrom, form.replaceFrom, setField]);

  useEffect(() => {
    if (!form.replaceTo.trim()) setField('replaceTo', defaultTo);
  }, [defaultTo, form.replaceTo, setField]);

  useEffect(() => {
    if (!form.replaceDuration.trim()) setField('replaceDuration', defaultDuration);
  }, [defaultDuration, form.replaceDuration, setField]);

  useEffect(() => {
    if (!form.replaceSteps.trim()) setField('replaceSteps', '6');
  }, [form.replaceSteps, setField]);

  return (
    <div className={PANEL_CLASS}>
      <DrawerHeader title="Replace Section" onBack={onBack} onClose={onClose} />
      <p style={DESC}>Replace only one section of &quot;{song.title}&quot;.</p>
      {!replaceRuntimeSupported ? <PrototypeNotice style={{ marginBottom: 14 }}>{replaceRuntimeNotice}</PrototypeNotice> : null}
      {/*
        THE TWO FIELDS WRAP, AND EACH MAY SHRINK.

        `flex: 1` alone sets flex-basis to 0 but leaves min-width at `auto`, which for
        a flex item containing a text input floors it at that input's default intrinsic
        width — around 170px in every engine. Two of those plus the 10px gap wants
        ~350px, and this panel is the centre column with 22px of padding either side:
        at 390px it had ~330px to give, so the second field hung over the edge and the
        Studio scrolled sideways.

        `minWidth: 0` lets them divide whatever the column has, and `flexWrap` is the
        floor under that — below about 250px two side-by-side time fields stop being
        legible, so they take a line each instead of both becoming nubs. Same pairing
        as every other two-up row in the workspace (TrimPanel, TransportBar).
      */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
          <label
            htmlFor="studio-replace-from"
            style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
          >
            From
          </label>
          <input
            id="studio-replace-from"
            value={form.replaceFrom}
            onChange={(e) => setField('replaceFrom', e.target.value)}
            className={FIELD_CLASS}
            style={input}
            disabled={!replaceRuntimeSupported}
          />
        </div>
        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
          <label
            htmlFor="studio-replace-to"
            style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
          >
            To
          </label>
          <input
            id="studio-replace-to"
            value={form.replaceTo}
            onChange={(e) => setField('replaceTo', e.target.value)}
            className={FIELD_CLASS}
            style={input}
            disabled={!replaceRuntimeSupported}
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
          <label
            htmlFor="studio-replace-duration"
            style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
          >
            Duration (seconds)
          </label>
          <input
            id="studio-replace-duration"
            type="number"
            min={6}
            max={190}
            step={1}
            inputMode="numeric"
            value={form.replaceDuration}
            onChange={(e) => setField('replaceDuration', e.target.value)}
            className={FIELD_CLASS}
            style={input}
            disabled={!replaceRuntimeSupported}
          />
        </div>
        <div style={{ flex: '1 1 120px', minWidth: 0 }}>
          <label
            htmlFor="studio-replace-steps"
            style={{ fontSize: 12, color: c.text2, display: 'block', margin: '0 0 6px' }}
          >
            Steps
          </label>
          <input
            id="studio-replace-steps"
            type="number"
            min={4}
            max={8}
            step={1}
            inputMode="numeric"
            value={form.replaceSteps}
            onChange={(e) => setField('replaceSteps', e.target.value)}
            className={FIELD_CLASS}
            style={input}
            disabled={!replaceRuntimeSupported}
          />
        </div>
      </div>
      <label htmlFor="studio-replace-prompt" style={{ ...LABEL, display: 'block' }}>
        Prompt
      </label>
      <textarea
        id="studio-replace-prompt"
        rows={3}
        value={form.replacePrompt}
        onChange={(e) => setField('replacePrompt', e.target.value)}
        className={FIELD_CLASS}
        style={TEXTAREA}
        disabled={!replaceRuntimeSupported}
      />
      <Btn full onClick={onGenerate} disabled={processing || !replaceRuntimeSupported}>
        <Scissors size={13} />
        {processing && processingActionKind === 'replace' ? 'Replacing section…' : 'Generate'}
      </Btn>
    </div>
  );
}

export interface StemsPanelProps {
  song: Song;
  processing: boolean;
  onBack: () => void;
  onClose: () => void;
  stemsReady: boolean;
  stemsLoading: boolean;
  stemsChecked: boolean;
  stemsProcessing: boolean;
  stemExpectedCount: number;
  stemLabels: string[];
  playingStemLabel: string | null;
  loadingStemLabel: string | null;
  shifts: number;
  onShiftsChange: (value: number) => void;
  onSplit: () => void;
  onPlayStem: (stem: string) => void;
  onDownloadStem: (stem: string) => void;
  onDownloadAll: () => void;
}

export function StemsPanel({
  song,
  processing,
  processingActionKind,
  onBack,
  onClose,
  stemsReady,
  stemsLoading,
  stemsChecked,
  stemsProcessing,
  stemExpectedCount,
  stemLabels,
  playingStemLabel,
  loadingStemLabel,
  shifts,
  onShiftsChange,
  onSplit,
  onPlayStem,
  onDownloadStem,
  onDownloadAll,
}: StemsPanelProps & { processingActionKind?: StudioActionKind | null }) {
  const hasStemDownloads = stemLabels.length > 0;

  return (
    <div className={PANEL_CLASS}>
      <DrawerHeader title="Split Stems" backLabel={stemsReady ? 'Timeline' : undefined} onBack={onBack} onClose={onClose} />
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 16px' }}>
        Extract individual stems from &quot;{song.title}&quot;. It takes about 45 seconds to
        extract a stem. For six or more stems, please exercise patience or leave it as a
        background task.
      </p>
      <label htmlFor="studio-stems-shifts" style={{ ...LABEL, display: 'block' }}>
        Shifts
      </label>
      <input
        id="studio-stems-shifts"
        type="number"
        min={1}
        max={10}
        step={1}
        value={shifts}
        onChange={(e) => {
          const next = Number.parseInt(e.target.value, 10);
          if (Number.isFinite(next)) onShiftsChange(Math.max(1, Math.min(10, next)));
        }}
        className={FIELD_CLASS}
        style={{ ...TEXTAREA, resize: undefined, marginBottom: 8 }}
      />
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 16px' }}>Choose an integer from 1 to 10. Higher values can improve separation at the cost of more processing.</p>
      {/* Above Split, not above the download rows: the split is the charge, and the
          downloads underneath it are free. */}
      <Btn full onClick={onSplit} disabled={processing || stemsProcessing || stemsReady || stemsLoading} style={{ marginBottom: hasStemDownloads || stemsChecked ? 16 : 0 }}>
        <Layers size={13} />
        {stemsLoading
          ? 'Loading Stems...'
          : stemsProcessing || (processing && processingActionKind === 'stems')
            ? `Splitting stems…${stemLabels.length > 0 && stemExpectedCount > 0 ? ` ${stemLabels.length}/${stemExpectedCount}` : ''}`
            : stemsReady
              ? 'Stems already split'
              : 'Split stems'}
      </Btn>
      {stemsChecked && !hasStemDownloads && !stemsReady && !stemsProcessing && !processing && (
        <p style={{ fontSize: 12, color: c.text2, margin: '0 0 16px', textAlign: 'center' }}>
          No stems found
        </p>
      )}
      {hasStemDownloads && (
        <>
          <p style={{ fontSize: 12, color: c.text2, margin: '0 0 8px' }}>Download</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {stemLabels.map((stemLabel) => (
              <div key={stemLabel} style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                <div style={{ flex: 1 }}>
                  <ToolButton
                    icon={<Music size={13} color={c.text2} />}
                    onClick={() => onDownloadStem(stemLabel)}
                    trailing={<Download size={13} color={c.text2} />}
                  >
                    {stemLabel}.wav
                  </ToolButton>
                </div>
                <button
                  type="button"
                  onClick={() => onPlayStem(stemLabel)}
                  aria-pressed={playingStemLabel === stemLabel}
                  aria-label={
                    loadingStemLabel === stemLabel
                      ? `Loading ${stemLabel}`
                      : playingStemLabel === stemLabel
                        ? `Pause ${stemLabel}`
                        : `Play ${stemLabel}`
                  }
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: '50%',
                    border: 'none',
                    background: c.accentFill,
                    color: '#1A1300',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                  }}
                >
                  {loadingStemLabel === stemLabel ? (
                    <Loader2 size={14} color="#1A1300" className="lyx-spin" />
                  ) : playingStemLabel === stemLabel ? (
                    <Pause size={14} color="#1A1300" />
                  ) : (
                    <Play size={14} color="#1A1300" style={{ marginLeft: 1 }} />
                  )}
                </button>
              </div>
            ))}
            {stemsReady && (
              <Btn variant="secondary" onClick={onDownloadAll} style={{ marginTop: 6 }}>
                <Download size={13} />
                Download ZIP
              </Btn>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export interface CoverPanelProps extends StudioToolPanelsProps {
  onGenerate: () => void;
  /** Cover export, shared with the Download Center. */
  onDownloadCover?: () => void;
  /** Artwork staged for preview, from studioStore. */
  pendingCover?: PendingCover | null;
  /** Receives an uploaded file. Validation happens in useStudio. */
  onUpload?: (file: File) => void;
  /** Clears the song's artwork. */
  onRemoveCover?: () => void;
  /** Commits the staged artwork to the song. */
  onApplyCover?: () => void;
  /** Throws the staged artwork away. */
  onDiscardCover?: () => void;
}

/**
 * COVER ART — the artwork box, and the controls that generate into it.
 *
 * Two routes to the same result and they are still equals — a generated gradient and
 * an image the user had designed are both simply the song's cover — but they are no
 * longer two panels. The artwork box takes the upload directly (click, tap or drop),
 * because it is the artwork; the form beside it is the generator's inputs. There is no
 * "or" divider any more, because there are no longer two halves to divide.
 *
 * Neither route applies anything. Both stage their result in the box, which carries the
 * Apply, the Discard and the Remove. See CoverPreview.
 */
export function CoverPanel({
  song,
  form,
  setField,
  processing,
  processingActionKind,
  onBack,
  onClose,
  onGenerate,
  onDownloadCover,
  pendingCover,
  onUpload,
  onRemoveCover,
  onApplyCover,
  onDiscardCover,
}: CoverPanelProps & { processingActionKind?: StudioActionKind | null }) {
  const [coverDropOver, setCoverDropOver] = useState(false);
  const coverDragDepth = useRef(0);
  const canUploadCover = Boolean(onUpload) && !processing;

  return (
    <div className={PANEL_CLASS}>
      <DrawerHeader title="Cover Art" onBack={onBack} onClose={onClose} />
      <p style={DESC}>
        Click the artwork to upload your own image, or generate one for &quot;{song.title}
        &quot;.
      </p>

      {/*
        The box sits above the form, not below the button. Cover art is a visual
        result, so the box is the subject of the panel and the controls are what you
        use on it — putting it under Generate would push it off the bottom on a short
        viewport at the moment it filled in.

        On a wide centre column the two sit side by side; see .lyx-cover-layout.
      */}
      <div
        className="lyx-cover-layout"
        onDragEnter={(event) => {
          if (!canUploadCover) return;
          event.preventDefault();
          coverDragDepth.current += 1;
          setCoverDropOver(true);
        }}
        onDragOver={(event) => {
          if (!canUploadCover) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={() => {
          coverDragDepth.current = Math.max(0, coverDragDepth.current - 1);
          if (coverDragDepth.current === 0) setCoverDropOver(false);
        }}
        onDrop={(event) => {
          coverDragDepth.current = 0;
          setCoverDropOver(false);
          if (event.defaultPrevented || !canUploadCover) return;
          event.preventDefault();
          const file = firstImageFrom(event.dataTransfer?.files ?? null);
          if (file && onUpload) onUpload(file);
        }}
        style={{
          borderRadius: 10,
          outline: coverDropOver ? `1px dashed ${c.accentBorder}` : 'none',
          outlineOffset: 6,
        }}
      >
        <CoverPreview
          song={song}
          processing={processing}
          onDownload={onDownloadCover}
          pending={pendingCover}
          onUpload={onUpload}
          onRemove={onRemoveCover}
          onApply={onApplyCover}
          onDiscard={onDiscardCover}
        />

        <div className="lyx-cover-form">
          <p style={LABEL}>Style</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
            {COVER_STYLES.map((style) => (
              <Chip
                key={style}
                label={style}
                active={form.coverStyle === style}
                onClick={() => setField('coverStyle', form.coverStyle === style ? null : style)}
              />
            ))}
          </div>
          <label htmlFor="studio-cover-prompt" style={{ ...LABEL, display: 'block' }}>
            Prompt (optional)
          </label>
          <textarea
            id="studio-cover-prompt"
            rows={2}
            value={form.coverPrompt}
            onChange={(e) => setField('coverPrompt', e.target.value)}
            className={FIELD_CLASS}
            style={TEXTAREA}
          />
          {/* Generating charges; uploading your own image does not, which is why this
              sits in the form beside the artwork box rather than under the whole panel. */}
              <Btn full onClick={onGenerate} disabled={processing}>
            <ImageIcon size={13} />
            {/* "Regenerate" once the song has artwork: the button is doing a
                different thing to the picture already applied. */}
            {processing && processingActionKind === 'cover'
              ? hasCoverArt(song)
                ? 'Regenerating artwork…'
                : 'Generating artwork…'
              : hasCoverArt(song)
                ? 'Regenerate'
                : 'Generate'}
          </Btn>

          {/* Points at the box for the other route, rather than repeating it as a
              second drop zone. The box says the same thing on itself; this is here
              because a reader working down the form should not have to guess that the
              picture above is where uploading happens. */}
          {onUpload && (
            <p style={{ fontSize: 11.5, color: c.text2, margin: '10px 0 0', lineHeight: 1.5 }}>
              Already have artwork? Click the cover above to replace it. Cover uploads are limited to {STUDIO_COVER_MAX_IMAGE_LABEL}.
            </p>
          )}
        </div>
      </div>

      {/* Says what the box is actually showing, and is careful to scope the claim:
          uploads are real images, so a blanket "not a real image" notice would be
          wrong the moment someone uses one.

          The reasons come from coverApi rather than being restated here, the way
          UploadPanel renders audioUploadApi's, so this copy cannot drift from what the
          boundary actually reports — and it shortens on its own the day a backend is
          configured. */}
      {/* <PrototypeNotice style={{ marginTop: 14 }}>
        Image generation is not wired up yet.{' '}
        {coverApi
          .support()
          .blockers.map((blocker) => blocker.summary)
          .join(' ')}{' '}
        The pattern is derived from this song&apos;s genre, mood and prompt, so it is the song&apos;s
        own — but it is a pattern, not a picture. Uploaded images are shown as-is.
      </PrototypeNotice> */}
    </div>
  );
}

export interface DownloadsPanelProps {
  song: Song;
  onBack: () => void;
  onClose: () => void;
  onDownloadAudio: (format: string) => void;
  onDownloadLyrics: () => void;
  onDownloadCover: () => void;
  showStemsDownload: boolean;
  onDownloadStems: () => void;
  onDownloadDetails: () => void;
}

export function DownloadsPanel({
  song,
  onBack,
  onClose,
  onDownloadAudio,
  onDownloadLyrics,
  onDownloadCover,
  showStemsDownload,
  onDownloadStems,
  onDownloadDetails,
}: DownloadsPanelProps) {
  const arrow = <Download size={13} color={c.text2} />;

  return (
    <div className={PANEL_CLASS}>
      <DrawerHeader title="Download Center" onBack={onBack} onClose={onClose} />
      <p style={{ fontSize: 12, color: c.text2, margin: '0 0 12px' }}>
        Everything available for &quot;{song.title}&quot;.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <ToolButton icon={<Music size={13} color={c.text2} />} onClick={() => onDownloadAudio('MP3')} trailing={arrow}>
          MP3
        </ToolButton>
        <ToolButton icon={<Music size={13} color={c.text2} />} onClick={() => onDownloadAudio('WAV')} trailing={arrow}>
          WAV
        </ToolButton>
        <ToolButton icon={<FileMusic size={13} color={c.text2} />} onClick={onDownloadLyrics} trailing={arrow}>
          Lyrics
        </ToolButton>
        <ToolButton icon={<ImageIcon size={13} color={c.text2} />} onClick={onDownloadCover} trailing={arrow}>
          Cover art
        </ToolButton>
        {showStemsDownload && (
          <ToolButton icon={<Layers size={13} color={c.text2} />} onClick={onDownloadStems} trailing={arrow}>
            Stems
          </ToolButton>
        )}
        <Btn variant="secondary" onClick={onDownloadDetails} style={{ marginTop: 6 }}>
          <Download size={13} />
          Download details (text)
        </Btn>
      </div>
    </div>
  );
}
